/**
 * Options for {@link generateRandomString}. All character-class flags default
 * to `true` except `symbols`, which defaults to `false` (passwords typically
 * opt in, salts include symbols explicitly).
 */
export interface GenerateRandomStringOptions {
  /** Total number of characters in the returned string. Must be >= 1. */
  length: number;
  /** Include lowercase letters (excludes `l` to avoid confusion with `1`/`I`). */
  lowercase?: boolean;
  /** Include uppercase letters (excludes `I` and `O` to avoid confusion with `1`/`l` and `0`). */
  uppercase?: boolean;
  /** Include digits (excludes `0` and `1` to avoid confusion with `O` and `I`/`l`). */
  numbers?: boolean;
  /** Include punctuation: `!@#$%^&*()-_=+[]{};:,.<>?`. */
  symbols?: boolean;
  /**
   * When `true`, the result is guaranteed to contain at least one character
   * from every enabled class. Useful for password-policy compliance.
   * Requires `length` >= number of enabled classes.
   */
  strict?: boolean;
}

// Confusable characters are deliberately excluded so generated strings remain
// readable when copied by hand or dictated: `O 0`, `I l 1`.
const LOWERCASE = 'abcdefghijkmnopqrstuvwxyz';
const UPPERCASE = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const NUMBERS = '23456789';
const SYMBOLS = '!@#$%^&*()-_=+[]{};:,.<>?';

/**
 * Generates a cryptographically secure random string from the configured
 * character classes.
 *
 * Randomness comes from `crypto.getRandomValues` (Web Crypto API). Each
 * character index is drawn with rejection sampling to avoid the modulo bias
 * that a plain `value % pool.length` would introduce on pool sizes that don't
 * evenly divide `2^32`.
 *
 * In `strict` mode the algorithm is: pick one character from each enabled
 * pool, fill the remaining slots from the union of all enabled pools, then
 * apply a cryptographically secure Fisher–Yates shuffle so the guaranteed
 * characters aren't predictably positioned.
 *
 * @throws {RangeError} If `length < 1`, no character class is enabled, or
 *   `strict` is `true` and `length` is smaller than the number of enabled
 *   classes.
 *
 * @example
 * // 16-char password with at least one of each class
 * generateRandomString({ length: 16, symbols: true, strict: true });
 *
 * @example
 * // 32-char salt without class guarantees
 * generateRandomString({ length: 32, symbols: true });
 */
export function generateRandomString(options: GenerateRandomStringOptions): string {
  const {
    length,
    lowercase = true,
    uppercase = true,
    numbers = true,
    symbols = false,
    strict = false,
  } = options;

  if (length < 1) {
    throw new RangeError('generateRandomString: length must be >= 1');
  }

  const pools: string[] = [];
  if (lowercase) pools.push(LOWERCASE);
  if (uppercase) pools.push(UPPERCASE);
  if (numbers) pools.push(NUMBERS);
  if (symbols) pools.push(SYMBOLS);

  if (pools.length === 0) {
    throw new RangeError('generateRandomString: at least one character class must be enabled');
  }

  if (strict && length < pools.length) {
    throw new RangeError(
      `generateRandomString: length (${length}) must be >= number of enabled classes (${pools.length}) when strict=true`
    );
  }

  const unionPool = pools.join('');

  if (!strict) {
    const out = new Array<string>(length);
    for (let i = 0; i < length; i++) {
      out[i] = unionPool[unbiasedRandomIndex(unionPool.length)];
    }
    return out.join('');
  }

  // Seed with one char per enabled class to satisfy the strict invariant,
  // then fill the remainder from the union pool and shuffle so the seeded
  // positions aren't predictably at the start of the string.
  const chars: string[] = pools.map(pool => pool[unbiasedRandomIndex(pool.length)]);
  for (let i = pools.length; i < length; i++) {
    chars.push(unionPool[unbiasedRandomIndex(unionPool.length)]);
  }
  return cryptoShuffle(chars).join('');
}

/**
 * Returns an integer in `[0, bound)` drawn from `crypto.getRandomValues`
 * without modulo bias.
 *
 * A naive `value % bound` skews the distribution unless `bound` divides
 * `2^32`. We discard samples in the top, non-divisible remainder
 * (`[max, 2^32)`) and retry, so every accepted value maps to exactly
 * `floor(2^32 / bound)` source values — yielding a uniform distribution.
 */
function unbiasedRandomIndex(bound: number): number {
  const max = Math.floor(0xFFFFFFFF / bound) * bound;
  const buf = new Uint32Array(1);
  let value: number;
  do {
    crypto.getRandomValues(buf);
    value = buf[0];
  } while (value >= max);
  return value % bound;
}

/**
 * In-place Fisher–Yates shuffle using a cryptographically secure index
 * source. Mutates and returns `items`.
 */
function cryptoShuffle<T>(items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = unbiasedRandomIndex(i + 1);
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}
